import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PincodeInput } from '../components/PincodeInput';

const VendorRegistrationForm = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifications, setVerifications] = useState({
    mobile: false,
    email: false,
    gst: false,
    bank: false
  });

  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    email: '',
    mobile: '',
    password: '',
    
    // OTP
    emailOtp: '',
    mobileOtp: '',
    
    // Business Details
    businessName: '',
    businessType: '',
    gstNumber: '',
    enrollmentId: '',
    businessAddress: '',
    city: '',
    state: '',
    pincode: '',
    
    // Support
    supportEmail: '',
    supportMobile: '',
    
    // Bank Details
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    
    // Commission
    commissionRate: '10'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const sendEmailOtp = async () => {
    setLoading(true);
    try {
      // Simulate OTP send
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({ title: 'OTP Sent', description: 'Check your email for verification code' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const sendMobileOtp = async () => {
    setLoading(true);
    try {
      // Simulate OTP send
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast({ title: 'OTP Sent', description: 'Check your mobile for verification code' });
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOtp = () => {
    // Simulate verification - accept any 6 digit code
    if (formData.emailOtp.length === 6) {
      setVerifications(prev => ({ ...prev, email: true }));
      toast({ title: 'Success', description: 'Email verified successfully!' });
    } else {
      toast({ title: 'Error', description: 'Invalid OTP', variant: 'destructive' });
    }
  };

  const verifyMobileOtp = () => {
    // Simulate verification
    if (formData.mobileOtp.length === 6) {
      setVerifications(prev => ({ ...prev, mobile: true }));
      toast({ title: 'Success', description: 'Mobile verified successfully!' });
    } else {
      toast({ title: 'Error', description: 'Invalid OTP', variant: 'destructive' });
    }
  };

  const verifyGst = async () => {
    setLoading(true);
    try {
      // Simulate GST verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (formData.gstNumber.length === 15) {
        setVerifications(prev => ({ ...prev, gst: true }));
        toast({ title: 'Success', description: 'GST verified successfully!' });
      } else {
        toast({ title: 'Error', description: 'Invalid GST number', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const verifyBankDetails = async () => {
    setLoading(true);
    try {
      // Simulate bank verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      if (formData.accountNumber && formData.ifscCode.length === 11) {
        setVerifications(prev => ({ ...prev, bank: true }));
        toast({ title: 'Success', description: 'Bank details verified successfully!' });
      } else {
        toast({ title: 'Error', description: 'Invalid bank details', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all verifications
    if (!verifications.mobile || !verifications.email) {
      toast({ title: 'Error', description: 'Please verify mobile and email', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      // Create vendor via API
      await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          mobile: formData.mobile,
          password: formData.password,
          role: 'vendor'
        })
      });

      toast({
        title: 'Vendor Registered!',
        description: 'Vendor account created successfully. Pending admin approval.'
      });
      
      navigate('/vendors');
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Step 1: Basic Information & Verification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter full name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="businessName">Business Name *</Label>
            <Input
              id="businessName"
              value={formData.businessName}
              onChange={(e) => handleChange('businessName', e.target.value)}
              placeholder="Enter business name"
            />
          </div>
        </div>

        {/* Email Verification */}
        <div className="space-y-2 p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center justify-between">
            <Label htmlFor="email">Email *</Label>
            {verifications.email && <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>}
          </div>
          <div className="flex gap-2">
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="email@example.com"
              disabled={verifications.email}
            />
            {!verifications.email && (
              <Button onClick={sendEmailOtp} disabled={loading || !formData.email}>
                Send OTP
              </Button>
            )}
          </div>
          {!verifications.email && (
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Enter 6-digit OTP"
                value={formData.emailOtp}
                onChange={(e) => handleChange('emailOtp', e.target.value)}
                maxLength={6}
              />
              <Button onClick={verifyEmailOtp} disabled={formData.emailOtp.length !== 6}>
                Verify
              </Button>
            </div>
          )}
        </div>

        {/* Mobile Verification */}
        <div className="space-y-2 p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center justify-between">
            <Label htmlFor="mobile">Mobile *</Label>
            {verifications.mobile && <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>}
          </div>
          <div className="flex gap-2">
            <Input
              id="mobile"
              value={formData.mobile}
              onChange={(e) => handleChange('mobile', e.target.value)}
              placeholder="+91 99999 99999"
              disabled={verifications.mobile}
            />
            {!verifications.mobile && (
              <Button onClick={sendMobileOtp} disabled={loading || !formData.mobile}>
                Send OTP
              </Button>
            )}
          </div>
          {!verifications.mobile && (
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Enter 6-digit OTP"
                value={formData.mobileOtp}
                onChange={(e) => handleChange('mobileOtp', e.target.value)}
                maxLength={6}
              />
              <Button onClick={verifyMobileOtp} disabled={formData.mobileOtp.length !== 6}>
                Verify
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            placeholder="Create a strong password"
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={() => setStep(2)} disabled={!verifications.email || !verifications.mobile}>
            Next: Business Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Step 2: Business Details & Verification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* GST Verification */}
        <div className="space-y-2 p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center justify-between">
            <Label htmlFor="gstNumber">GST Number *</Label>
            {verifications.gst && <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>}
          </div>
          <div className="flex gap-2">
            <Input
              id="gstNumber"
              value={formData.gstNumber}
              onChange={(e) => handleChange('gstNumber', e.target.value.toUpperCase())}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              disabled={verifications.gst}
            />
            {!verifications.gst && (
              <Button onClick={verifyGst} disabled={loading || formData.gstNumber.length !== 15}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="enrollmentId">Enrollment ID (Optional)</Label>
          <Input
            id="enrollmentId"
            value={formData.enrollmentId}
            onChange={(e) => handleChange('enrollmentId', e.target.value)}
            placeholder="Enter enrollment ID if applicable"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="businessAddress">Business Address *</Label>
          <Textarea
            id="businessAddress"
            value={formData.businessAddress}
            onChange={(e) => handleChange('businessAddress', e.target.value)}
            placeholder="Complete business address"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <PincodeInput
              id="pincode"
              label="Pincode *"
              value={formData.pincode}
              onChange={(v) => handleChange('pincode', v)}
              onAutoFill={({ city, state, country }) => {
                if (city) handleChange('city', city);
                if (state) handleChange('state', state);
              }}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Auto-filled from pincode"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State *</Label>
            <Input
              id="state"
              value={formData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              placeholder="Auto-filled from pincode"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="supportEmail">Support Email *</Label>
            <Input
              id="supportEmail"
              type="email"
              value={formData.supportEmail}
              onChange={(e) => handleChange('supportEmail', e.target.value)}
              placeholder="support@business.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supportMobile">Support Mobile *</Label>
            <Input
              id="supportMobile"
              value={formData.supportMobile}
              onChange={(e) => handleChange('supportMobile', e.target.value)}
              placeholder="+91 99999 99999"
            />
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(1)}>
            Previous
          </Button>
          <Button onClick={() => setStep(3)} disabled={!verifications.gst}>
            Next: Bank Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <CardTitle>Step 3: Bank Details & Commission</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Bank Verification */}
        <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <Label>Bank Account Details *</Label>
            {verifications.bank && <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bankName">Bank Name</Label>
              <Input
                id="bankName"
                value={formData.bankName}
                onChange={(e) => handleChange('bankName', e.target.value)}
                placeholder="State Bank of India"
                disabled={verifications.bank}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accountHolderName">Account Holder Name</Label>
              <Input
                id="accountHolderName"
                value={formData.accountHolderName}
                onChange={(e) => handleChange('accountHolderName', e.target.value)}
                placeholder="As per bank records"
                disabled={verifications.bank}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accountNumber">Account Number</Label>
              <Input
                id="accountNumber"
                value={formData.accountNumber}
                onChange={(e) => handleChange('accountNumber', e.target.value)}
                placeholder="1234567890"
                disabled={verifications.bank}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ifscCode">IFSC Code</Label>
              <Input
                id="ifscCode"
                value={formData.ifscCode}
                onChange={(e) => handleChange('ifscCode', e.target.value.toUpperCase())}
                placeholder="SBIN0001234"
                maxLength={11}
                disabled={verifications.bank}
              />
            </div>
          </div>

          {!verifications.bank && (
            <Button onClick={verifyBankDetails} disabled={loading} className="w-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verify Bank Details
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="commissionRate">Commission Rate (%) *</Label>
          <Input
            id="commissionRate"
            type="number"
            value={formData.commissionRate}
            onChange={(e) => handleChange('commissionRate', e.target.value)}
            min="0"
            max="100"
            step="0.1"
          />
          <p className="text-xs text-gray-500">Platform commission on each sale</p>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(2)}>
            Previous
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !verifications.bank}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Submit Vendor Registration
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Add New Vendor</h1>
          <p className="text-gray-600 mt-2">Complete vendor registration with verification</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </DashboardLayout>
  );
};

export default VendorRegistrationForm;
